import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const sectionLinks = [
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "markets", label: "Markets" },
  { id: "faq", label: "FAQ" },
];

const pageLinks = [{ to: "/industries", label: "Industries" }];

const companyLinks = [
  { to: "/global-network", label: "Global Network" },
  { to: "/leadership", label: "Leadership" },
  { to: "/compliance", label: "Compliance" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (onHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || !onHome
          ? "backdrop-blur-xl bg-white/90 border-b border-altin-border shadow-[0_2px_20px_rgba(26,29,32,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
          aria-label="Altin Link — Home"
        >
          <img
            src="/altin-logo-sm.png"
            alt="Altin Link Ltd. Şti."
            className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {sectionLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-xs text-altin-ink hover:text-altin-gold transition-colors uppercase tracking-[0.18em]"
            >
              {l.label}
            </button>
          ))}

          {pageLinks.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              data-testid={`nav-page-${p.label.toLowerCase()}`}
              className="text-xs text-altin-ink hover:text-altin-gold transition-colors uppercase tracking-[0.18em]"
            >
              {p.label}
            </Link>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setCompanyOpen(true)}
            onMouseLeave={() => setCompanyOpen(false)}
          >
            <button
              data-testid="nav-company-toggle"
              onClick={() => setCompanyOpen((v) => !v)}
              className="flex items-center gap-1 text-xs text-altin-ink hover:text-altin-gold transition-colors uppercase tracking-[0.18em]"
            >
              Company
              <CaretDown size={12} weight="light" />
            </button>
            <AnimatePresence>
              {companyOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-full pt-4 w-52"
                >
                  <div className="bg-white border border-altin-border shadow-lg py-2">
                    {companyLinks.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-page-${c.label.toLowerCase()}`}
                        className="block px-5 py-3 text-xs uppercase tracking-[0.18em] text-altin-ink hover:bg-altin-bg hover:text-altin-gold transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        <button
          data-testid="nav-contact-cta"
          onClick={() => go("contact")}
          className="hidden lg:inline-flex items-center bg-altin-ink text-white px-6 py-3 text-xs tracking-[0.2em] uppercase hover:bg-altin-gold transition-colors duration-300"
        >
          Contact Us
        </button>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-altin-ink"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} weight="light" /> : <List size={26} weight="light" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden border-t border-altin-border bg-white/95 backdrop-blur-xl max-h-[80vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {sectionLinks.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-base text-altin-ink hover:text-altin-gold transition-colors uppercase tracking-[0.18em]"
                >
                  {l.label}
                </button>
              ))}
              <div className="pt-2 border-t border-altin-border" />
              {[...pageLinks, ...companyLinks].map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-page-${c.label.toLowerCase()}`}
                  className="text-left text-base text-altin-ink hover:text-altin-gold transition-colors uppercase tracking-[0.18em]"
                >
                  {c.label}
                </Link>
              ))}
              <button
                data-testid="mobile-contact-cta"
                onClick={() => go("contact")}
                className="mt-2 bg-altin-ink text-white px-6 py-4 text-xs tracking-[0.2em] uppercase hover:bg-altin-gold transition-colors"
              >
                Contact Us
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
